/**
 * The sandbox's dev inspector (SANDBOX_DEV_INSPECTOR, local development only): an icon on a repl or
 * bash call that opens a drawer with the cell's code, the tool calls it made and its output. Off,
 * nothing renders. This module stays small: the drawer is its own chunk, loaded on open.
 */
import { Loader2, SearchCode } from "lucide-react";
import { lazy, memo, Suspense, useState } from "react";
import { useSelector } from "react-redux";

import { Button } from "@/components/ui/button";
import type { RootState } from "@/store/store";

const SandboxInspectorDrawer = lazy(() => import("./SandboxInspectorDrawer"));

/** The tools whose calls run a cell in the sandbox. */
export const INSPECTABLE_TOOLS: ReadonlySet<string> = new Set(["repl", "bash"]);

export const useSandboxInspector = () =>
  useSelector((state: RootState) => state.auth.user?.sandbox_dev_inspector === true);

/** The Inspect icon for one cell's call (a tool line, later the sandbox_cell part). */
export const SandboxInspectButton = memo(function SandboxInspectButton({
  callId,
}: {
  callId: string;
}) {
  const on = useSandboxInspector();
  const [open, setOpen] = useState(false);
  if (!on) return null;
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5 shrink-0 text-muted-foreground [&_svg]:size-3.5"
        aria-label="Inspect sandbox cell"
        title="Inspect sandbox cell"
        onClick={() => setOpen(true)}
      >
        <SearchCode aria-hidden />
      </Button>
      {open && (
        <Suspense fallback={<CellLoading />}>
          <SandboxInspectorDrawer callId={callId} onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
});

export const CellLoading = () => (
  <p className="flex items-center gap-2 text-sm text-muted-foreground">
    <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden /> Loading the cell…
  </p>
);
